
import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import VitalScoreHeader from './VitalScoreHeader';
import VitalScoreGrid from './VitalScoreGrid';
import BmiStatusCard from './BmiStatusCard';
import { getScoreText, getBmiStatusText, getBmiHealthStatus } from '@/utils/vitalScoreTextUtils';

interface VitalScoreCardProps {
  vitalScore: any;
  currentWeight: number;
  currentHeight: number;
}

const VitalScoreCard: React.FC<VitalScoreCardProps> = React.memo(({
  vitalScore,
  currentWeight,
  currentHeight
}) => {
  console.log('VitalScoreCard rendering:', {
    totalScore: vitalScore?.total,
    hasValidStructure: !!(vitalScore?.energyRest && vitalScore?.nutrition && vitalScore?.movement && vitalScore?.hydrationSun),
    vitalScore
  });

  // Memoizar cálculos de BMI para evitar recálculos
  const bmiData = useMemo(() => {
    const displayBmi = currentHeight > 0 ? 
      (currentWeight / Math.pow(currentHeight/100, 2)) : 0;
    
    return {
      displayBmi,
      bmiStatus: getBmiStatusText(displayBmi),
      bmiHealthStatus: getBmiHealthStatus(displayBmi)
    };
  }, [currentWeight, currentHeight]);
  
  // Memoizar score data
  const scoreData = useMemo(() => {
    const totalScore = typeof vitalScore?.total === 'number' ? vitalScore.total : 0;
    const scoreText = getScoreText(totalScore);
    const hasValidStructure = !!(vitalScore?.energyRest && 
                               vitalScore?.nutrition && 
                               vitalScore?.movement && 
                               vitalScore?.hydrationSun);
    
    return { totalScore, scoreText, hasValidStructure };
  }, [vitalScore]);

  // Validar que vitalScore tenga la estructura correcta
  if (!vitalScore) {
    return (
      <Card className="mb-6 shadow-sm">
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            Cargando datos del vital score...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 shadow-xl overflow-hidden border-0 bg-gradient-to-br from-purple-600/5 via-blue-500/5 to-purple-600/5 backdrop-blur-sm">
      <div 
        className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-blue-500/10 opacity-50" 
        style={{ zIndex: 0 }}
      />
      
      <VitalScoreHeader 
        totalScore={scoreData.totalScore} 
        scoreText={scoreData.scoreText} 
      />
      
      <CardContent className="relative space-y-4" style={{ zIndex: 1 }}>
        {scoreData.hasValidStructure ? (
          <VitalScoreGrid vitalScore={vitalScore} />
        ) : (
          <div className="py-4 text-center text-gray-400">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        )}
        
        <BmiStatusCard
          currentWeight={currentWeight}
          currentHeight={currentHeight}
          displayBmi={bmiData.displayBmi}
          bmiStatus={bmiData.bmiStatus}
          bmiHealthStatus={bmiData.bmiHealthStatus}
        />
      </CardContent>
    </Card>
  );
});

VitalScoreCard.displayName = 'VitalScoreCard';

export default VitalScoreCard;
